import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { useEffect, useRef, useState } from "react";
import { LetterDto } from "../../types/Rows";
import { headers } from "../../api/headers";
import ScientiaHeader from "../../components/ScientiaHeader";
import { ROBOTO_SOURCES } from "../report/document/pdfFonts";
import { Footer } from "../followupletter/footer";

// Te same pliki Roboto co raport (lokalnie, nie z CDN); nazwa rodziny bez zmian
Font.register({ family: "Times New Roman", fonts: ROBOTO_SOURCES });

// Create styles
const styles = StyleSheet.create({
  headers: {
    display: "flex",
    flexDirection: "row",
  },
  // wartość zajmuje resztę wiersza i się zawija (długie tytuły badań wychodziły poza stronę)
  headerValue: {
    flex: 1,
  },
  page: {
    fontFamily: "Roboto",
    fontSize: 12,
    paddingBottom: 65,
    paddingHorizontal: 35,
    backgroundColor: "white",
  },
  mainSection: {
    textAlign: "center",
    fontWeight: "semibold",
  },
  section: {
    marginBottom: 5,
    marginTop: 5,
    fontSize: 10,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "semibold",
  },
  importantParagraph: {
    fontSize: 14,
    fontWeight: "bold",
    marginTop: 10,
  },
  pageNumber: {
    position: "absolute",
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: "center",
    color: "grey",
  },
  dateContainer: {
    fontWeight: "bold",
    display: "flex",
    flexDirection: "row",
    width: "100%",
    justifyContent: "flex-end",
  },
});

const capitalizeWords = (str) =>
  str
    .split(" ")
    .map((word) => {
      return word
        .split("-")
        .map(
          (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
        )
        .join("-");
    })
    .join(" ");

const formatDate = (date) => {
  const d = new Date(date);
  let day = d.getDate().toString().padStart(2, "0");
  let month = (d.getMonth() + 1).toString().padStart(2, "0");
  const year = d.getFullYear();

  return `${day}/${month}/${year}`;
};

const fmtNullable = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  return formatDate(new Date(iso));
};
var logoUrl = require("../../assets/logo-scientia.png");

export const CL_SECTIONS: { key: string; label: string }[] = [
  { key: "header",        label: "Nagłówek (tytuł, badacz, protokół, ośrodek)" },
  { key: "main-text",     label: "Treść główna (potwierdzenie wizyty)" },
  { key: "research-team", label: "Zespół badawczy" },
  { key: "visit-reason",  label: "Cel i zakres wizyty" },
  { key: "cro-team",      label: "Kontakt zespołu CRO" },
  { key: "signature",     label: "Podpis" },
];

export const ConfirmationletterDocGenerator = ({
  confirmationLetter,
  author,
  footerText,
  versionText,
  title,
  visitType,
  sections,
}: {
  confirmationLetter: LetterDto;
  author: string;
  footerText?: string;
  versionText?: string;
  title: string;
  visitType: string;
  sections?: Set<string>;
}) => {
  const show = (key: string) => !sections || sections.has(key);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View fixed>
          <ScientiaHeader />
        </View>
        <View style={styles.dateContainer}>
          <Text>Data: {formatDate(new Date())}</Text>
        </View>

        {show("header") && (
          <>
            <View style={styles.headers}>
              <Text style={{ fontWeight: "semibold" }}>Tytuł badania: </Text>
              <Text style={styles.headerValue}> {title}</Text>
            </View>
            <View style={styles.headers}>
              <Text style={{ fontWeight: "semibold" }}>Typ wizyty: </Text>
              <Text style={styles.headerValue}> {visitType}</Text>
            </View>
            <View style={styles.headers}>
              <Text style={{ fontWeight: "semibold" }}>
                Imię i nazwisko głównego badacza:{" "}
              </Text>
              <Text style={styles.headerValue}> {confirmationLetter.mainResearcher}</Text>
            </View>
            <View style={styles.headers}>
              <Text style={{ fontWeight: "semibold" }}>Numer Protokołu: </Text>
              <Text style={styles.headerValue}>{confirmationLetter.protocolNumber}</Text>
            </View>
            <View style={styles.headers}>
              <Text style={{ fontWeight: "semibold" }}>Numer Ośrodka: </Text>
              <Text style={styles.headerValue}>{confirmationLetter.resortNumber}</Text>
            </View>
            <View style={[styles.mainSection, { marginTop: 16 }]}>
              <Text>List potwierdzający wizytę w Ośrodku</Text>
            </View>
          </>
        )}

        {show("main-text") && (
          <View style={styles.section}>
            <Text>{confirmationLetter.introductionText},</Text>
            <Text style={{ marginTop: 6 }}>
              niniejszy list ma na celu potwierdzić wizytę{" "}
              {confirmationLetter.visitType} w Ośrodku w dn.{" "}
              {confirmationLetter.visitDay}. Planuję rozpocząć wizytę ok. godz.{" "}
              {confirmationLetter.visitHour}.
            </Text>
          </View>
        )}

        {show("research-team") && (
          <View style={styles.section}>
            <Text>
              Podczas wizyty powinni być obecni Członkowie Zespołu, a w
              szczególności:
            </Text>
            <Text>{confirmationLetter.researchTeam}</Text>
          </View>
        )}

        {show("visit-reason") && (
          <View style={styles.section}>
            <Text>Podczas wizyty będę potrzebować dostępu do:</Text>
            <Text>{confirmationLetter.visitReason}</Text>
          </View>
        )}

        {show("cro-team") && (
          <View style={styles.section}>
            <Text>
              W przypadku jakichkolwiek pytań proszę o kontakt z naszym zespołem
              CRO:
            </Text>
            <Text>{confirmationLetter.croTeam}</Text>
          </View>
        )}

        {show("signature") && (
          <View style={styles.section}>
            <Text>Z poważaniem,</Text>
            <Text>{author}</Text>
          </View>
        )}

        <Footer footerText={footerText} versionText={versionText} />
      </Page>
    </Document>
  );
};
